"use client";

/**
 * Broad-search fast-results page (Step 5, 2026-08-01).
 *
 * Renders the curated best 5-6 products for a broad query, EXACTLY as the
 * approved /mockup/fast-results design: the real SearchAgainBar, the /c/list
 * ListCard row style, the .cmp-surface tokens, Geist fonts, and the real
 * SiteHeader (inherited from the app layout). The image slot carries the real
 * Google Shopping thumbnail (CSP allows https images), same as ListCard, with
 * a graceful fallback when it is missing or fails to load.
 *
 * Prices shown are the lowest LISTED price. Tapping a card runs the existing
 * True Final Price comparison for that product (from_chip skips clarify and
 * searches directly), on a TRIMMED brand-model-type query so the deep fan-out
 * matches many retailers, not just the one whose long title we copied.
 */

import type { Route } from "next";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";

import type { BroadProduct } from "@/lib/api/broad";
import {
  BROAD_PRICE_FOOTNOTE,
  BROAD_SUBTITLE,
  cleanTitleForSearch,
} from "@/lib/broad-format";

import { Disclosures } from "./Disclosures";
import { LIST_CARD_CLASS, ListCardContent } from "./ListCardContent";
import { SearchAgainBar } from "./SearchAgainBar";
import { SearchingSceneOverlay } from "./SearchingSceneOverlay";

interface Props {
  query: string;
  products: BroadProduct[];
  postcode: string;
  fetchedAt: string;
}

/** "office desk" -> "Best office desks" (matches the approved mockup heading). */
function toHeading(query: string): string {
  const q = query.trim();
  return `Best ${q.endsWith("s") ? q : `${q}s`}`;
}

export function FastResults({ query, products, postcode, fetchedAt }: Props) {
  const router = useRouter();
  const [picked, setPicked] = useState(false);
  const [, startTransition] = useTransition();

  // Tap a card -> that product's comparison. A WARMED pick (comparison_slug set
  // by background warming) goes straight to its saved /c/<slug> page (instant,
  // cache-only, cannot loop). Otherwise fall back to a live /compare search on
  // the CLEAN title.
  const compare = (p: { title: string; comparison_slug?: string | null }) => {
    setPicked(true);
    if (p.comparison_slug) {
      startTransition(() => router.push(`/c/${p.comparison_slug}` as Route));
      return;
    }
    const q = cleanTitleForSearch(p.title);
    startTransition(() =>
      router.push(
        `/compare?q=${encodeURIComponent(q)}&postcode=${encodeURIComponent(postcode)}&from_chip=1` as Route,
      ),
    );
  };

  if (picked) {
    return (
      <SearchingSceneOverlay loaded={0} failed={0} total={0} isDone={false} />
    );
  }

  return (
    <main className="cmp-surface mx-auto min-h-screen max-w-3xl bg-background px-6 pb-16">
      <SearchAgainBar initialQuery={query} />

      <header className="mt-6">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {toHeading(query)}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {BROAD_SUBTITLE}
        </p>
      </header>

      <section className="mt-6 space-y-3">
        {products.map((p, i) => (
          <button
            key={p.title + i}
            type="button"
            onClick={() => compare(p)}
            className={LIST_CARD_CLASS}
          >
            <ListCardContent product={p} />
          </button>
        ))}
      </section>

      <p className="mt-5 text-center text-sm text-muted-foreground">
        {BROAD_PRICE_FOOTNOTE}
      </p>

      <Disclosures
        postcode={postcode}
        fetchedAt={fetchedAt}
        cacheHit
        priceBasis="listed"
      />
    </main>
  );
}
